import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
// import { logger } from "redux-logger"; // Disabled - uncomment to enable redux-logger

import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { setupListeners } from "@reduxjs/toolkit/query";
// import { userApiQuery } from "../Services/api.user";
import { autoCompleteApiQuery } from "../Services/auto-complete.services";
// import { apiLocationBasedSearch } from "../Services/api.location.based.search";
// import { apiPartners } from "../Services/api.partners";
import { apiProperty } from "../Services/api.property";
// import { apiLocationServices } from "../Services/api.localServices";
import { apiAuthSlice } from "../Services/api.auth.slice";
import authReducer from "../Redux/authReducer";
import propertyReducer from "../Redux/propertyReducer";
// import { apiWishlistSlice } from "../Services/api.wishlist.slice";
import { apiUserSlice } from "../Services/api.user.slice";
import { apiAdminSlice } from "../Services/admin/api.admin.slice";
import { apiDocumentsSlice } from "../Services/api.documents.slice";
import { apiContactSlice } from "../Services/api.contact.slice";



// reducers
import adminAuthReducer from "../Redux/admin/adminReducer"

export const reducers = combineReducers({
    // [userApiQuery.reducerPath]: userApiQuery.reducer,
    [autoCompleteApiQuery.reducerPath]: autoCompleteApiQuery.reducer,
    // [apiLocationBasedSearch.reducerPath]: apiLocationBasedSearch.reducer,
    // [apiPartners.reducerPath]: apiPartners.reducer,
    [apiProperty.reducerPath]: apiProperty.reducer,
    // [apiLocationServices.reducerPath]: apiLocationServices.reducer,
    [apiUserSlice.reducerPath]: apiUserSlice.reducer,

    // login
    [apiAuthSlice.reducerPath]: apiAuthSlice.reducer,
    auth: authReducer,
    propertyReducer: propertyReducer,

    // [apiWishlistSlice.reducerPath]: apiWishlistSlice.reducer,


    // admin-slice
    [apiAdminSlice.reducerPath]: apiAdminSlice.reducer,
    adminAuth: adminAuthReducer,

    // documents-slice
    [apiDocumentsSlice.reducerPath]: apiDocumentsSlice.reducer,

    // contact-slice
    [apiContactSlice.reducerPath]: apiContactSlice.reducer,
});

const persistConfig = {
    key: "root",
    version: 1,
    storage,
    timeout: 10,
    blacklist: [
        autoCompleteApiQuery.reducerPath,
        apiProperty.reducerPath,
        apiAuthSlice.reducerPath,
        apiUserSlice.reducerPath,
        apiAdminSlice.reducerPath,
        apiDocumentsSlice.reducerPath,
        apiContactSlice.reducerPath,
        // propertyReducer caches fetched property lists (e.g. locationBased).
        // Persisting it served stale data — including a missing isClaimed flag,
        // so claimed cards kept showing the unclaimed lock. Keep it in-memory so
        // lists (and claim status) are always fetched fresh.
        "propertyReducer",
    ],
};

// On logout, wipe ALL slice state — including the RTK Query caches, whose
// cache keys don't include the user id. Without this, queries cached for one
// account (e.g. getWishlistWithDetails) survive into the next account that
// signs in on the same device. combineReducers re-initializes every slice,
// then applies the logout action from a clean slate.
export const rootReducer = (
    state: ReturnType<typeof reducers> | undefined,
    action: any
) => {
    if (action.type === "auth/logout") {
        // Reset user-scoped state (incl. RTK Query caches) on logout so the
        // previous user's data can't leak into the next session. PRESERVE the
        // separate admin session: a user logout — or a 401 on a user endpoint,
        // which also dispatches auth/logout — must not wipe a signed-in admin.
        state = state
            ? ({
                  adminAuth: state.adminAuth,
                  [apiAdminSlice.reducerPath]: state[apiAdminSlice.reducerPath],
              } as ReturnType<typeof reducers>)
            : undefined;
    }
    return reducers(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false,
            immutableCheck: false, // Disabled to improve performance with large state
        }).concat([
            // userApiQuery.middleware,
            autoCompleteApiQuery.middleware,
            // apiLocationBasedSearch.middleware,
            // apiPartners.middleware,
            apiProperty.middleware,
            // apiLocationServices.middleware,
            // apiLocationServices.middleware,
            apiAuthSlice.middleware,
            // apiWishlistSlice.middleware,

            apiUserSlice.middleware,
            apiAdminSlice.middleware,
            apiDocumentsSlice.middleware,
            apiContactSlice.middleware,
            // logger, // Disabled - uncomment to enable redux-logger
        ]),
    devTools: true,
});

setupListeners(store.dispatch);

export const useAppDispatch: () => typeof store.dispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<
    ReturnType<typeof store.getState>
> = useSelector;
export type RootState = ReturnType<typeof store.getState>;
